import { Component, h, Prop, Element, Method, Watch } from '@stencil/core';
import type { GridSizes } from '../../interface';

const STATEOPTS = ['col-span', 'col-start', 'row-span', 'row-start'];

/**
 * Grid items to be used with [grid](/story/nano-components-grid) elements
 */
@Component({
  tag: 'nano-grid-item',
  styleUrl: 'grid-item.scss',
  shadow: true,
})
export class GridItem {
  private currGridSizes: GridSizes[] = [null];

  @Element() private el: HTMLNanoGridItemElement;
  /**
   * How to position this item within it's parent grid at different break points. Examples:
   * xl-col-span-2
   * l-col-start-2
   * xxl-row-span-2
   * m-row-start-2
   */
  @Prop() gridStates: string = '';

  @Watch('gridStates')
  updateGridClasses() {
    this.applyChildrenClasses();
  }

  /**
   * Called by parent grid to trigger new classes
   * @internal
   */
  @Method()
  async changeBP(newGridSizes: GridSizes[]) {
    if (this.currGridSizes === newGridSizes) return;
    this.currGridSizes = newGridSizes;

    if (!this.gridStates.length) return;
    this.applyChildrenClasses();
  }

  private applyChildrenClasses() {
    let stateArr: Array<string> = [''];
    let itemState: string = '';
    let found: any;
    let classes: Array<string>;

    // item states come in 4 possible flavours at every breakpoint. E.g.
    // xl-col-span-2
    // xl-col-start-2
    // xl-row-span-2
    // xl-row-start-2

    // clear all previous grid state classnames
    classes = this.el.className
      .split(' ')
      .filter((c) => !c.startsWith('nano-grid-'));
    this.el.className = classes.join(' ').trim();

    // get all potential states this element can have
    const itemStates = this.gridStates.split(' ');

    // loop through all potential state options: col & row span & start
    STATEOPTS.forEach((stateOpt) => {
      // reset found flag
      found = false;

      // loop through all the current valid breakpoints / sizes backwards because
      // we only care about the item's state at the largest current breakpoint.
      this.currGridSizes
        .slice()
        .reverse()
        .forEach((size) => {
          if (found || !size) return;
          found = itemStates.find(
            (state) => state.indexOf(`${size.size}-${stateOpt}`) === 0
          );

          // found a state at this current size. apply class.
          if (found) {
            // clear previous grid state classname
            classes = this.el.className
              .split(' ')
              .filter((c) => !c.includes('nano-grid-${stateOpt}'));
            this.el.className = classes.join(' ').trim();

            stateArr = found.split('-');

            // remove the size of the state
            stateArr.shift();
            itemState = stateArr.join('-');

            // add new class
            this.el.classList.add(`nano-grid-${itemState}`);
          }
        });
    });
  }

  render() {
    return <slot />;
  }
}
